import { useRef, useEffect, useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

function useEmbedInject(html) {
    const containerRef = useRef(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!containerRef.current || !html) return;
        const container = containerRef.current;
        container.innerHTML = '';

        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;

        Array.from(wrapper.childNodes).forEach(node => {
            if (node.nodeName === 'SCRIPT') {
                const script = document.createElement('script');
                Array.from(node.attributes).forEach(attr => script.setAttribute(attr.name, attr.value));
                script.textContent = node.textContent;
                container.appendChild(script);
            } else {
                container.appendChild(node.cloneNode(true));
            }
        });

        setLoaded(true);

        return () => { container.innerHTML = ''; };
    }, [html]);

    return { containerRef, loaded };
}

export default function EmbedSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const variant = config.variant || 'simple';
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);

    const html = content.html || '';
    const height = content.height || '500';
    const { containerRef } = useEmbedInject(html);

    if (!html) return (
        <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <div className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                <div className="w-full rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center" style={{minHeight: `${height}px`}}>
                    <svg className="w-14 h-14 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>
                    <p className="text-sm text-gray-400 font-medium">Embed / Widget externo</p>
                    <p className="text-xs text-gray-300 mt-1">Pega el codigo HTML despues de agregar</p>
                </div>
            </div>
        </section>
    );

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const embedBox = (
        <div ref={containerRef}
            className="w-full overflow-hidden rounded-xl"
            style={{ minHeight: `${height}px` }} />
    );

    /* ══════════════════ VARIANT: with-description ══════════════════ */
    if (variant === 'with-description') {
        const badge = content.badge || '';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="rounded-2xl border p-6 sm:p-8 lg:p-10" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', backgroundColor: s.dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)' }}>
                        {badge && (
                            <span className="inline-block text-xs font-semibold uppercase tracking-wider rounded-full px-3 py-1 mb-4"
                                style={{ backgroundColor: s.accent + '18', color: s.accent }}>
                                {badge}
                            </span>
                        )}
                        {content.title && (
                            <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: s.text }}>{content.title}</h2>
                        )}
                        {content.subtitle && (
                            <p className="text-base mb-6 max-w-2xl" style={{ color: s.textSub }}>{content.subtitle}</p>
                        )}
                        <div className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            {embedBox}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ══════════════════ VARIANT: split ══════════════════ */
    if (variant === 'split') {
        const features = (content.features || '').split('\n').filter(Boolean);
        const ctaText = content.cta_text || '';
        const ctaLink = content.cta_link || '#';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
                        {/* Left: embed */}
                        <div className={`w-full lg:w-1/2 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            {embedBox}
                        </div>
                        {/* Right: text content */}
                        <div className={`w-full lg:w-1/2 flex flex-col justify-center transition-all duration-500 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            {content.title && (
                                <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: s.text }}>{content.title}</h2>
                            )}
                            {content.subtitle && (
                                <p className="text-base mb-5" style={{ color: s.textSub }}>{content.subtitle}</p>
                            )}
                            {features.length > 0 && (
                                <ul className="space-y-2 mb-6">
                                    {features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                                            <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                                            </svg>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {ctaText && (
                                <a {...resolveLinkProps(ctaLink)} className="inline-block px-6 py-3 rounded-lg text-sm font-semibold text-white text-center transition-transform hover:scale-105"
                                    style={{ backgroundColor: s.accent }}>
                                    {ctaText}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ══════════════════ VARIANT: simple (default) ══════════════════ */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {(content.title || content.tag) && (
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                )}
                <div className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    {embedBox}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
